import React, {useState} from "react";
import {observer} from "mobx-react";
import {useStores} from "../../Hooks";
import GeneralDialog from "./GeneralDialog";
import Utility from "../../Utility";
import {Checkbox, TextField} from "@mui/material";
import SortableList, {DRAG_CLASS} from "../Generic/SortableList";
import MenuIcon from "@mui/icons-material/Menu";
import QuickActionDropdown from "../Shared/QuickActionDropdown";
import {quickMenuActionEnum} from "../../enums";
import DeleteIconOutlined from "@mui/icons-material/DeleteOutlined";
import AddIcon from "@mui/icons-material/Add";
import ConditionalComponent from "../Shared/ConditionalComponent";

const EditSitePrepChecklistModal = observer(() => {
    const {modalStore, generalStore} = useStores();
    const [checklist, setChecklist] = useState(modalStore.modalData.sitePrepData?.SitePrepChecklist || []);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [menuIndex, setMenuIndex] = useState(0);
    const [newItemText, setNewItemText] = useState("");
    const [newItemChecked, setNewItemChecked] = useState(false);

    const isTemplate = modalStore.modalData.isTemplate;
    const isAddNew = !!modalStore.modalData.isAddNew;

    const addChecklistAndFinish = async () => {
        modalStore.modalData.sitePrepData.SitePrepChecklist = checklist;
        if (isTemplate) {
            await modalStore.modalData.onFinishAddTemplate();
            Utility.sleep(200).then(() => modalStore.modalData = {});
        }
    }

    const onConfirm = async () => {
        modalStore.modalData.sitePrepData.SitePrepChecklist = checklist;

        if (!!newItemText && newItemText.trim()) {
            modalStore.modalData.sitePrepData.SitePrepChecklist.push({
                ListItem: newItemText,
                Checked: newItemChecked
            });
            setNewItemText("");
            setNewItemChecked(false);
        }

        modalStore.isEditSitePrepChecklistModalOpen = false;
        if (isAddNew) {
            await addChecklistAndFinish();
        } else await modalStore.modalData.updateSitePrep();
    }

    const onSortEnd = ({oldIndex, newIndex}) => {
        const newChecklist = Utility.arrayMove(checklist, oldIndex, newIndex);
        setChecklist(newChecklist);
    }

    const toggleOpenMenu = (anchor, index) => {
        setMenuAnchor(anchor);
        setMenuIndex(index);
    }

    const handleChecklistItemChange = (updatedValue, index, prop) => {
        const updatedList = checklist;
        const updateItem = checklist[index];
        updateItem[prop] = updatedValue;
        updatedList.splice(index, 1, updateItem);
        setChecklist(updatedList);
    }

    const addNewItem = () => !!newItemText && addItem({
        ListItem: newItemText,
        Checked: newItemChecked
    }, checklist.length);

    const addItem = (item, index) => {
        const updatedList = checklist;
        const newItem = {...item};
        delete newItem._id;
        updatedList.splice(index, 0, newItem);
        setChecklist(updatedList);
        setNewItemText("");
        setNewItemChecked(false);
    }

    const duplicateItem = (item, index) => {
        addItem(item, index + 1);
        toggleOpenMenu(null, index);
    }

    const deleteItem = index => {
        const updatedList = checklist;
        updatedList.splice(index, 1);
        setChecklist(updatedList);
        setNewItemText("");
        setNewItemChecked(false);
        toggleOpenMenu(null, index);
    }

    const checklistItems = checklist.map((item, i) =>
        <div className='site-prep checklist-item' key={i}>
            <div className={DRAG_CLASS}>
                <MenuIcon className={DRAG_CLASS}/>
            </div>
            <ConditionalComponent if={!isTemplate}>
                <Checkbox className='site-prep-checkbox' checked={item.Checked} onChange={e => handleChecklistItemChange(e.target.checked, i, 'Checked')}/>
            </ConditionalComponent>
            <TextField
                className='checklist-item-name'
                value={item.ListItem}
                onChange={e => handleChecklistItemChange(e.target.value, i, 'ListItem')}
                variant='outlined'
                multiline
                maxRows={2}
            />
            <div className={`three-dots-container${!!menuAnchor && menuIndex === i ? ' selected' : ''}`}>
                <QuickActionDropdown
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: generalStore.langDir === 'rtl' ? "left" : "right",
                    }}
                    parentControlled
                    toggleMenu={(anchor) => toggleOpenMenu(anchor, i)}
                    menuOpen={!!menuAnchor && menuIndex === i}
                    menuAnchor={menuAnchor}
                    dotsAnchor='end'
                    horizontalDots
                    menuActions={{
                        [quickMenuActionEnum.DUPLICATE]: {callback: () => duplicateItem(item, i)},
                        [quickMenuActionEnum.DELETE]: {
                            callback: () => deleteItem(i),
                            classStr: ' delete-action',
                            MenuIcon: DeleteIconOutlined
                        },
                    }}
                />
            </div>
        </div>
    )

    const renderAddItem = () => {
        return (<div className='site-prep checklist-item add-item'>
                <div className={`add-icon${!newItemText ? ' inactive' : ''}`} onClick={addNewItem}>
                    <AddIcon/>
                </div>
                <ConditionalComponent if={!isTemplate}>
                    <ConditionalComponent if={!!newItemText}>
                        <Checkbox className='site-prep-checkbox' checked={newItemChecked.Checked} onChange={e => setNewItemChecked(e.target.checked)}/>
                    </ConditionalComponent>
                    <ConditionalComponent if={!newItemText}>
                        <div className='checkbox-filler'/>
                    </ConditionalComponent>
                </ConditionalComponent>
                <TextField
                    className='checklist-item-name'
                    value={newItemText}
                    onChange={e => setNewItemText(e.target.value)}
                    placeholder={generalStore.translate('newItem')}
                    variant='outlined'
                    multiline
                    maxRows={2}
                />
            </div>
        )
    }

    const renderBody = () => {
        return (
            <div className='checklist-container'>
                <SortableList
                    items={checklistItems}
                    onSortEnd={onSortEnd}
                    dragClass={DRAG_CLASS}
                />
                {renderAddItem()}
            </div>
        )
    }

    const onEnter = () => {
        setChecklist(modalStore.modalData.sitePrepData?.SitePrepChecklist || []);
        setNewItemText("");
        setNewItemChecked(false);
    }

    return (
        <GeneralDialog
            openControl='isEditSitePrepChecklistModalOpen'
            className={'procedure-option-dialog site-prep-options checklist'}
            onEnter={onEnter}
            title={<div>{generalStore.translate('checklist')} <span
                className='site-prep-checklist-name'>{modalStore.modalData.sitePrepData?.ProcedureName || ""}</span>
            </div>}
            divider
            body={renderBody()}
            noCancel={true}
            isConfirmDisabled={false}
            confirmText={generalStore.translate(isAddNew ? 'finish' : 'save')}
            onConfirm={onConfirm}
            extraClose
            goBackControl={isAddNew ? 'isSelectSitePrepFusionFilesModalOpen' : ''}
        />
    );
});

export default EditSitePrepChecklistModal;